import React, { useState, useEffect } from 'react';

import { compose } from "redux";
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import Placeholder from '../Placeholder';
import withFiltering from "../../hocs/withFiltering";
import withSorting from "../../hocs/withSorting";
import SchoolYearSelect from '../SchoolYearSelect';
import withSchoolYear from '../../hocs/withSchoolYear';
import Filters from '../Filters/Filters';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import FlashMessage from '../FlashMessage/FlashMessage';
import { dependentDuck, duckify, filterDuck, singleDuck } from "../../utils/duckify";
import queryString from "query-string";
import { apiFetch, titleize } from '../../utils';
import {
  reviewSchoolYears as reviewSchoolYearsDuck,
  reviewCriteria as reviewCriteriaDuck,
  reviewFields as reviewFieldsDuck,
  grades as gradesDuck,
  modals
} from "../../ducks";
import ContactList from '../ContactList/ContactList';
import { defaultColumn } from '../ContactList/ColumnDefinitions';


const ReaderScoresReport = ({
  onSchoolYearChange,
  schoolYearId,
  allDucksReady,
  reviewSchoolYears,
  reviewFields,
  reviewCriteria,
  defaultYear,
  grades,
  fields,
  showModal,
  filters,
  setFilters,
  sort,
  onSort,
}) => {

  const [report, setReport] = useState({});
  // This starts in the ready state so that it doesn't switch to placeholder briefly every time the report re-renders
  const [ready, setReady] = useState(true)
  const searchParams = new URLSearchParams(location.search);
  const [warningMessage, setWarningMessage] = useState(null)

  // The timeout is here so that if fetching the data takes less than a second, we don't switch to placeholder and flash the screen white
  useEffect(() => {
    const timeout = setTimeout(() => setReady(false), 800)
    apiFetch.get({}, `reporting/reader_scores_report?${searchParams}`)
      .then(json => {
        setReport(json.reader_scores_report)})
      .catch((error) => (showModal('UnexpectedError')))
      .finally(() => {
        clearTimeout(timeout);
        setReady(true);
      })
  }, [location.search]);

  useEffect(() => {
    if (defaultYear.warning_message) {
      setWarningMessage(defaultYear.warning_message)
    }
  }, [defaultYear.warning_message])

  // We have grade as a default filter so let's not include it twice
  const filteredFields = reviewFields ? reviewFields.filter((f) => !f.field_canonical_name.includes('grade')) : []
  const fieldFilters = filteredFields.map((field) => Object.fromEntries([['solr_prompt', field.field_canonical_name], ['display_name', field.field_name], ['options', field.field_options]]));

  const defaultColumns = [
    defaultColumn({
      header: 'Student Name',
      value: 'user_name',
      sortName: 'user_name'
    })
  ];

  const criteriaColumns = reviewCriteria && reviewCriteria.map((c) =>
    defaultColumn({
      header: c.name,
      value: c.name,
    }))


  return (
    <React.Fragment>
      {warningMessage && <FlashMessage type={'warning'} message={warningMessage}/>}
      <h2>Reader Scores Report</h2>
      <BulkActions>
        <BulkAction title='Export' icon={faSignOut}>
          <BulkExportAction
            title='Download as CSV'
            type='ReaderScoresReportJob'
            location={location}
            url='/api/reporting/reader_scores_report/export'
          />
        </BulkAction>
      </BulkActions>
      {
        reviewSchoolYears.ready && (
          <SchoolYearSelect
            onSelect={(year) => onSchoolYearChange(year)}
            schoolYearId={schoolYearId || defaultYear.id}
            schoolYears={reviewSchoolYears}
          />
        )
      }
      <Filters
        fields={fieldFilters.concat([
          {
            solr_prompt: 'grade_id',
            display_name: 'Grade',
            options: grades.filter((g) => g.offered).map(g => [titleize(g.name), g.id])
          }
        ])}
        selected={filters}
        submitFilters={setFilters}
        itemsName='Readers'
      />
      <Placeholder.loading ready={allDucksReady && ready}>

        <ContactList
          className='review-current-table reader-scores-report'
          columns={defaultColumns.concat(criteriaColumns)}
          contacts={Object.values(report)}
          emptyMessage='There are no reviewers in this list'
          onSort={onSort}
          sort={sort}
        />
      </Placeholder.loading>
    </React.Fragment>

  )
}


const mapDucksToProps = ({ filters, location, schoolYearId }) => {
  const params = queryString.parse(location.search);
  const defaultYear = singleDuck(reviewSchoolYearsDuck, 'current');
  return {
    defaultYear: defaultYear,
    reviewSchoolYears: reviewSchoolYearsDuck,
    grades: gradesDuck,
    reviewCriteria: dependentDuck(defaultYear, ({ value: defaultYear }) => filterDuck(reviewCriteriaDuck, { school_year_id: params.school_year_id || defaultYear.id })),
    reviewFields: dependentDuck(defaultYear, ({ value: defaultYear }) => filterDuck(reviewFieldsDuck, {
      filter_option: true,
      school_year_id: params.school_year_id || defaultYear.id
    })),
  }
}

const mapDispatchToProps = (dispatch) => ({
  showModal: (type) => dispatch(modals.actions.show(type))
});

export default compose(
  withSorting({ column: 'name', order: 'asc' }),
  withFiltering,
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(() => '')
)(ReaderScoresReport);
